import {useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {Group, Stack} from "@mantine/core";
import slugify from "@sindresorhus/slugify";
import type {PortalWidget, PortalWidgetLink} from "./types";
import {usePortalData} from "./usePortalData";
import {LinkChip} from "../../components/LinkChip";
import {WidgetCard} from "../../components/WidgetCard";
import {getLinkTypeMeta} from "../content/typeIcons";
import classes from "./DashboardView.module.css";

export const ANDRE_SLUG = "andre";

export function groupingSlug(
    name: string | undefined,
    id: number | string
): string {
    return name ? slugify(name) : `g-${id}`;
}

function widgetMatchesProductIds(
    widget: PortalWidget,
    productIds: Set<string>
): boolean {
    if (!widget.conditions || widget.conditions.length === 0) return false;
    return widget.conditions.some((group) =>
        group.some((id) => productIds.has(String(id)))
    );
}

export function PortalsPage() {
    const {t} = useTranslation();
    const {relevantWidgets, relevantGroupings, isLoading} = usePortalData();
    // Only animate when this mount began while data was still loading (genuine
    // first load). On tab-switch remounts the data is cached, so isLoading
    // starts false and the captured ref stays false — no replay.
    const shouldAnimate = useRef(isLoading).current;

    const sections = useMemo(() => {
        const claimed = new Set<number>();
        const groupingSections: {
            slug: string;
            name: string;
            widgets: PortalWidget[];
        }[] = [];

        for (const g of relevantGroupings) {
            const productIds = g.products_json
                ? new Set(JSON.parse(g.products_json) as string[])
                : new Set<string>();
            const matched = relevantWidgets.filter((w) => {
                if (claimed.has(w.id)) return false;
                return widgetMatchesProductIds(w, productIds);
            });
            for (const w of matched) claimed.add(w.id);
            if (matched.length > 0 && g.name) {
                groupingSections.push({
                    slug: groupingSlug(g.name, g.id),
                    name: g.name,
                    widgets: matched,
                });
            }
        }

        const unclaimed = relevantWidgets.filter((w) => !claimed.has(w.id));

        return {groupingSections, unclaimed};
    }, [relevantWidgets, relevantGroupings]);

    // Render blank (space stays reserved by the layout) until data loads, then
    // stagger each heading/card in top-to-bottom for a domino fade. The delay
    // index runs across the whole page and is capped so the tail stays short.
    if (isLoading) return null;

    let order = 0;
    const animClass = shouldAnimate ? classes.contentAnimate : undefined;
    const nextDelay = () =>
        shouldAnimate
            ? {animationDelay: `${Math.min(order++, 12) * 30}ms`}
            : undefined;

    const renderWidget = (widget: PortalWidget) => (
        <div key={widget.id} className={animClass} style={nextDelay()}>
            <WidgetCard
                title={widget.title}
                titleUrl={widget.title_target_url}
                description={widget.description}
            >
                {widget.links.length > 0 && (
                    <Group gap="xs" wrap="wrap">
                        {widget.links
                            .filter((link: PortalWidgetLink) => link.targetUrl)
                            .map((link: PortalWidgetLink) => {
                                const meta = getLinkTypeMeta(link.targetUrl);
                                return (
                                    <LinkChip
                                        key={link.targetUrl}
                                        href={link.targetUrl || "#"}
                                        tooltip={t(meta.labelKey, {
                                            defaultValue: meta.label,
                                        })}
                                    >
                                        {link.title}
                                    </LinkChip>
                                );
                            })}
                    </Group>
                )}
            </WidgetCard>
        </div>
    );

    return (
        <Stack gap={0} className={classes.portalsList}>
            {sections.groupingSections.map((section) => (
                <section
                    key={section.slug}
                    id={`grouping-${section.slug}`}
                    data-grouping-slug={section.slug}
                    className={classes.groupingSection}
                >
                    <h2
                        className={`${classes.groupingHeading} ${animClass ?? ""}`}
                        style={nextDelay()}
                    >
                        {section.name}
                    </h2>
                    {section.widgets.map(renderWidget)}
                </section>
            ))}
        </Stack>
    );
}
